/**
 * 결제 수단 조합 카탈로그 조회 훅 (#217/#218, β-1b).
 *
 * 외부 리뷰 P1-2 반영: react-query 미도입 프로젝트이므로 useState + useEffect + apiClient 패턴 사용
 * (기존 useAllStores.ts, usePickupMenuData.ts 선례).
 *
 * 모듈 수준 캐시: 카탈로그는 마이그레이션 외 변경 없으므로 세션 내 한 번만 로드.
 * retry 경로: 에러 후 재시도 가능 (Codex 리뷰 P1-3, P1-5 반영).
 *
 * β-1b 단계에서는 훅 선언만 — 소비처 없음. β-2b (CheckoutPage) 에서 사용 시작.
 */
import { useCallback, useEffect, useState } from 'react';
import { paymentMethodsApi } from '@/lib/api/paymentMethods';
import type { PaymentMethodCombination } from '@/lib/api/types';
import { logger } from '@/utils/logger';

let cachedCombinations: PaymentMethodCombination[] | null = null;
let inflightPromise: Promise<PaymentMethodCombination[]> | null = null;

interface UsePaymentMethodCombinationsReturn {
  combinations: PaymentMethodCombination[];
  loading: boolean;
  error: Error | null;
  /** 에러 후 재시도 트리거. 캐시 무효화 + useEffect 재실행. */
  retry: () => void;
}

export function usePaymentMethodCombinations(): UsePaymentMethodCombinationsReturn {
  const [combinations, setCombinations] = useState<PaymentMethodCombination[]>(
    cachedCombinations ?? []
  );
  const [loading, setLoading] = useState(cachedCombinations === null);
  const [error, setError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (cachedCombinations !== null) {
      setCombinations(cachedCombinations);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    // Cache publication, failure capture and cleanup belong to the request,
    // not to each subscriber (including cancelled StrictMode subscriptions).
    if (inflightPromise === null) {
      const request = paymentMethodsApi.getCombinations()
        .then((data) => {
          if (inflightPromise === request) cachedCombinations = data;
          return data;
        })
        .catch((err: unknown) => {
          logger.error('[payment-method-combinations] 카탈로그 로드 실패', err);
          throw err;
        })
        .finally(() => {
          if (inflightPromise === request) inflightPromise = null;
        });
      inflightPromise = request;
    }
    const promise = inflightPromise;

    promise
      .then((data) => {
        if (!cancelled) {
          setCombinations(data);
          setLoading(false);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [retryNonce]);

  const retry = useCallback(() => {
    // Codex P1-1 반영: inflightPromise 는 useEffect 내 `?? 신규생성` 로직에 위임.
    // retry 에서 직접 null 화하면 동시 호출 시 singleton 가드가 순간 무효화되어 중복 요청 발생.
    cachedCombinations = null;
    setError(null);
    setLoading(true);
    setRetryNonce((n) => n + 1);
  }, []);

  return { combinations, loading, error, retry };
}

/**
 * @internal Vitest 전용. 프로덕션 번들에서는 `undefined` 로 치환되어 함수 본문 제거됨.
 * Vite 가 `import.meta.env.MODE` 를 빌드 타임 상수로 인라인하므로 dead code elimination 적용.
 * 테스트 측 사용: `resetCache?.()` (optional chaining) 권장.
 */
export const __resetPaymentMethodCombinationsCacheForTesting:
  | (() => void)
  | undefined =
  import.meta.env.MODE === 'test'
    ? () => {
        cachedCombinations = null;
        inflightPromise = null;
      }
    : undefined;
